import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  useCreateDepartment,
  useDepartmentDetail,
  useUpdateDepartment,
} from "@/hooks/useDepartments";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

export default function DepartmentFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");
  const canWriteDepartments =
    user?.permissions?.includes(PERMISSIONS.DEPARTMENTS_WRITE) ?? false;

  const detailQ = useDepartmentDetail(id);
  const createMut = useCreateDepartment();
  const updateMut = useUpdateDepartment();

  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (detailQ.data) {
      setName(detailQ.data.name || "");
    }
  }, [detailQ.data]);

  function goBack() {
    navigate("/cadastros/departamentos");
  }

  async function handleSave() {
    if (isViewing) return;

    if (!name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateMut.mutateAsync({ id: id!, body: { name } });
        toast.success("Departamento atualizado");
      } else if (isCreating) {
        await createMut.mutateAsync({ name });
        toast.success("Departamento cadastrado");
      }

      navigate("/cadastros/departamentos");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar departamento");
    } finally {
      setSaving(false);
    }
  }

  if ((isEditing || isViewing) && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isViewing
                  ? "Visualizar Departamento"
                  : isEditing
                  ? "Editar Departamento"
                  : "Novo Departamento"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isViewing
                  ? "Consulta dos dados do departamento"
                  : isEditing
                  ? "Atualize os dados do departamento"
                  : "Preencha os dados para cadastrar o departamento"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              {isViewing ? "Voltar" : "Cancelar"}
            </Button>

            {isViewing ? (
              <PermissionButton
                permission={PERMISSIONS.DEPARTMENTS_WRITE}
                onClick={() => navigate(`/cadastros/departamentos/${id}/editar`)}
              >
                Editar
              </PermissionButton>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados do departamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-xl">
            <Label>Nome *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Recursos Humanos"
              className="mt-1"
              disabled={isViewing}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
